import "./index.css";
import "./ProductComponentBox.lovable.css";
import { DuplicateButton } from "../../../../components/DuplicateButton";
import plusIcon from "../../../../assets/design-revamp-product/product-icons/plus-icon-product.svg";
import ProductModal from "../ProductModal";
import { useState, useCallback } from "react";
import checkIcon from "../../../../assets/design-revamp-product/product-icons/checkbox-product.svg";
import defaultCheck from "../../../../assets/design-revamp-product/product-icons/unselected-product-checkbox.svg";
import { ShieldCheck, Info } from "lucide-react";

// EPD / LCA lock status for product list badge: "draft" | "locked" | "verified_locked" (from backend when available)
const ProductComponentBox = ({
  productId,
  name,
  subgroup,
  variationValues,
  prodNum,
  isAddNew,
  year,
  subgroupId,
  business,
  subgroups,
  isAR,
  isSelected,
  onSelect,
  noBtn,
  assessmentType = "PCF", // Product's assessment type: "LCA" or "PCF". EPD badge only shown for LCA.
  lcaLockStatus, // "draft" | "locked" | "verified_locked" – when set, show status badge on card
  variant = "default", // "default" | "lovable" — rectangular shadcn/Lovable-style card
}) => {
  const isLovable = variant === "lovable";
  const isLCA = assessmentType === "LCA";
  const [open, setOpen] = useState(false);
  const handleDuplicateClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleBoxClick = useCallback(() => {
    if (isAddNew) {
      setOpen(true);
    }
  }, [isAddNew]);

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect();
    }
  };

  const EllipsisText = ({ text, maxLength = 6 }) => {
    const truncatedText =
      text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

    return <span>{truncatedText}</span>;
  };

  const rootClass = [
    isLovable ? "pcb pcb--lovable" : "",
    isLovable && isAddNew ? "pcb--add-new" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass || undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...(isLovable
          ? {
              width: "176px",
              minHeight: isAddNew ? "228px" : "228px",
              height: "auto",
            }
          : {
              width: "171px",
              height: "210px",
            }),
        ...(isAddNew && !isLovable ? { marginLeft: "12px" } : {}),
        ...(isAddNew && isLovable ? { marginLeft: "8px" } : {}),
        cursor: "pointer",
      }}
      onClick={handleBoxClick}
    >
      <div className="product-component-title">
        {name ? name : "Product name"}
      </div>
      {/* LCA / PCF type badge – shown on every product card */}
      {!isAddNew && (
        <div
          style={{
            position: "absolute",
            top: isLovable ? "46px" : "30px",
            left: isLovable ? "8px" : "8px",
            zIndex: 24,
            display: "inline-flex",
            alignItems: "center",
            padding: "0.125rem 0.5rem",
            fontSize: isLovable ? "10px" : "9px",
            fontWeight: 600,
            borderRadius: "6px",
            border: "1px solid",
            textTransform: "uppercase",
            letterSpacing: "0.02em",
            ...(isLCA
              ? {
                  background: isLovable
                    ? "hsl(173 58% 36%)"
                    : "rgb(4 120 87 / 0.9)",
                  color: "white",
                  borderColor: "transparent",
                }
              : {
                  background: isLovable
                    ? "hsl(32 95% 44%)"
                    : "rgb(217 119 6 / 0.9)",
                  color: "white",
                  borderColor: "transparent",
                }),
          }}
        >
          {isLCA ? "LCA" : "PCF"}
        </div>
      )}
      {/* EPD / LCA status badge – only for LCA products. Verified – Locked (purple), Locked, or Draft (grey). */}
      {!isAddNew && isLCA && (
        <div
          className="product-component-status-badge"
          style={{
            position: "absolute",
            top: isLovable ? "46px" : "20px",
            right: isLovable ? "8px" : "4px",
            zIndex: 25,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            fontSize: isLovable ? "9px" : "10px",
            fontWeight: 600,
            borderRadius: "6px",
            border: "1px solid",
            ...((lcaLockStatus || "draft") === "verified_locked"
              ? {
                  background: isLovable
                    ? "hsl(262 83% 58%)"
                    : "rgba(139, 92, 246, 0.9)",
                  color: "white",
                  borderColor: isLovable
                    ? "hsl(262 83% 70%)"
                    : "rgba(167, 139, 250, 0.5)",
                }
              : (lcaLockStatus || "draft") === "locked"
                ? isLovable
                  ? {
                      background: "hsl(215 25% 93%)",
                      color: "hsl(222 47% 11%)",
                      borderColor: "hsl(214 32% 91%)",
                    }
                  : {
                      background: "rgba(255, 255, 255, 0.25)",
                      color: "rgba(255, 255, 255, 0.95)",
                      borderColor: "rgba(255, 255, 255, 0.4)",
                    }
                : isLovable
                  ? {
                      background: "hsl(210 40% 96%)",
                      color: "hsl(215 16% 40%)",
                      borderColor: "hsl(214 32% 91%)",
                    }
                  : {
                      background: "rgba(156, 163, 175, 0.85)",
                      color: "rgba(255, 255, 255, 0.95)",
                      borderColor: "rgba(209, 213, 219, 0.5)",
                    }),
          }}
        >
          {(lcaLockStatus || "draft") === "verified_locked" ? (
            <>
              <ShieldCheck size={12} strokeWidth={2.5} />
              <span>Verified – Locked</span>
            </>
          ) : (lcaLockStatus || "draft") === "locked" ? (
            <>
              <ShieldCheck size={12} strokeWidth={2.5} />
              <span>Locked</span>
            </>
          ) : (
            <>
              <Info size={12} strokeWidth={2.5} />
              <span>Draft</span>
            </>
          )}
        </div>
      )}
      <div
        className="product-component-body"
        style={
          isLovable
            ? isAR
              ? { backgroundColor: "hsl(210 40% 96%)" }
              : {}
            : {
                backgroundColor: isAR
                  ? "rgba(102, 70, 80, 0.8)"
                  : "rgba(221, 210, 216, 1)",
              }
        }
      >
        {isAddNew ? (
          <>
            <p style={{ fontSize: "14px" }}>Add Product</p>
            <img
              src={plusIcon}
              width={80}
              height={80}
              alt="Add new product"
              style={{ margin: "0 auto" }}
            />
          </>
        ) : (
          <>
            <p>{subgroup || ""}</p>
            {variationValues?.length > 0 &&
              variationValues.map((category, categoryIndex) => (
                <div key={`category-${categoryIndex}`}>
                  {category?.values?.map((value, valueIndex) => (
                    <p key={`value-${categoryIndex}-${valueIndex}`}>
                      {value.value}
                    </p>
                  ))}
                </div>
              ))}
          </>
        )}
      </div>
      <div className="product-component-bottom">
        <span>#</span>
        {prodNum ? <EllipsisText text={prodNum} /> : ""}
      </div>
      {!isAddNew && !isAR && (
        <DuplicateButton id={productId} onClick={handleDuplicateClick} />
      )}
      {isAR && !noBtn && (
        <div
          style={{
            position: "absolute",
            bottom: "12px",
            right: "-3px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected ? (
            <img
              src={checkIcon}
              width={25}
              height={25}
              alt="Selected Checkbox"
              onClick={handleCheckboxClick}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <img
              src={defaultCheck}
              width={25}
              height={25}
              alt="Unselected Checkbox"
              onClick={handleCheckboxClick}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
      )}
      {open && (
        <ProductModal
          onClose={handleCloseModal}
          year={year}
          subgroupId={subgroupId}
          business={business}
          subgroups={subgroups}
          assessmentType={assessmentType}
        />
      )}
    </div>
  );
};

export default ProductComponentBox;
